import React, { useEffect, useState } from 'react';
import { Button, Card, Col, notification, Row, Space, Tooltip, Typography } from 'antd';
import { useResponsive } from "../../utils/use-responsive";
import { PlusOutlined, QuestionCircleFilled } from "@ant-design/icons";
import { ConnectionCardModal } from './connection-card-modal';
import { UploadDocuments } from "../documents/upload-documents";
import Cookies from "universal-cookie";
import { useDocuments } from '../../context/documents.context';

const { Title, Text } = Typography;

export const ConnectionCard: React.FC<{
    title?: string;
    tooltip?: string;
    connected?: string;
    icon?: any;
    button?: string;
    optional?: boolean;
  }> = ( { title, tooltip, connected, icon, button, optional = false } ) => {

    const { isXs } = useResponsive();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { getDocuments } = useDocuments();
    const [listOfDocuments, setListOfDocuments] = useState<[]>();
    const [page, setPage] = useState<string>('allDocuments');
    const [isBack, setIsBack] = useState<boolean>(false);

    const cookies = new Cookies();
    const companyId = cookies.get('companyId');

    function fetchDocuments( companyId: string ) {
      getDocuments.execute(companyId).then(( data: any ) => {
        setListOfDocuments(data.documents);
        if (data.documents.length === 0) {
          setIsModalOpen(false);
        }
      }).catch(() => {
        setListOfDocuments([]);
      });
    }

    useEffect(() => {
      if (button === 'Upload' && companyId !== undefined) {
        fetchDocuments(companyId);
      }
    }, [companyId]);  // eslint-disable-line react-hooks/exhaustive-deps


    const showModal = () => {
      if (button === 'Upload' && listOfDocuments?.length === 0) {
        notification.error({
          message: 'No documents available to upload',
          description: 'Please contact us for more information',
        });
        return;
      } else {
        setIsModalOpen(true);
      }
    };

    function showOptionalOrIcon() {
      if (!isXs && !optional) {
        return <img
          alt="Icon"
          src={icon}
        />
      } else if (!isXs && optional) {
        return <Row justify='space-between'>
          <img
            alt="Icon"
            src={icon}
          />
          <Text type="secondary">Optional</Text>
        </Row>
      }
    }

    function getTitle() {
      if (button === 'Upload') {
        switch (page) {
          case 'allDocuments':
            return 'Upload your business documents';
          case 'showRequiredDocuments':
            return 'Required documents';
          case 'showAdditionalDocuments':
            return 'Additional documents';
          default:
            return '';
        }
      }
      return title;
    }

    function getSubtitle() {
      if (button === 'Upload') {
        switch (page) {
          case 'showRequiredDocuments':
            return 'These items must be provided in order to finalise our review of your application.';
          case 'showAdditionalDocuments':
            return "These items aren't required but will help with our review of your application.";
          default:
            return '';
        }
      }
      return '';
    }

  function getContactUsWithText(text: string) {
    return <>
      {text}
      {' '}
      <a href="mailto:busdev@financefair.com">
      <Typography.Text style={{color: '#FF930F', fontSize: '0.9rem', textDecoration: 'underline'}}>
        Contact Us
      </Typography.Text>
    </a>
      </>;
  }

  function getDescription() {
    if (button === 'Upload') {
      switch (page) {
        case 'allDocuments':
          return getContactUsWithText("If you have issues providing the required information or have any queries please don't hesitate to");
        case 'showRequiredDocuments':
          return getContactUsWithText('If you are having trouble getting any of this information or have a query on exactly what is required, please');
        case 'showAdditionalDocuments':
          return getContactUsWithText('If you are having trouble getting any of this information or have a query on exactly what is required, please');
        default:
          return '';
      }
    }
      return '';
    }

    function getContent() {
      if (button === 'Upload') {
        return <UploadDocuments listOfDocuments={listOfDocuments || []}
                                onChange={( page: string ) => setPage(page)}
                                isBack={isBack}
        />
      }
      return <></>;
    }


    function goBack() {
      setPage('allDocuments');
      setIsBack(true);
      fetchDocuments(companyId);
    }

    return (
      <Col xs={24} sm={12} md={12} lg={8} xl={6}>
        <Card style={{ borderRadius: '8px' }}>
          <Space direction={'vertical'} size={'middle'} className={'full-width'}>
            {showOptionalOrIcon()}

            <Space.Compact direction={'vertical'} className={'full-width'}>
              <Space direction={'horizontal'} size={'small'} className={'ant-row-space-between'}>
                <Space direction={'horizontal'} size={'small'}>
                  <Title level={4} style={{ margin: 0 }}>
                    {title}
                  </Title>
                  <Tooltip placement="top" title={tooltip}>
                    <QuestionCircleFilled style={{ fontSize: '18px' }}/>
                  </Tooltip>
                </Space>
                {isXs && !optional && (
                  <img
                    alt="Icon"
                    src={icon}
                  />
                )}
                {isXs && optional && (
                  <Text type="secondary">Optional</Text>
                )}
              </Space>

              <Text type="secondary">{connected}</Text>
            </Space.Compact>
            <Button
              block type="default"
              size="large"
              style={{ backgroundColor: '#F2F3F4', borderRadius: '6px' }}
              onClick={showModal}
            >
              {button} {<PlusOutlined/>}
            </Button>
            <ConnectionCardModal
              show={isModalOpen}
              title={getTitle()}
              subtitle={getSubtitle()}
              description={getDescription()}
              content={button === 'Upload' ? getContent() : <></>}
              onClose={() => setIsModalOpen(false)}
              onBack={() => goBack()}
              isBack={button === 'Upload' && (page === 'showRequiredDocuments' || page === 'showAdditionalDocuments')}
              isSmallTitle={button === 'Upload' && (page === 'showRequiredDocuments' || page === 'showAdditionalDocuments')}
            ></ConnectionCardModal>
          </Space>
        </Card>
      </Col>
    );
  }
;
